/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key used to store storefront card data for recently viewed products */
  static #DETAILS_KEY = 'hdRecentlyViewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {{ id?: string | number, handle?: string, title?: string, url?: string, image?: string, price?: string }} [productData]
   */
  static addProduct(productId, productData = {}) {
    productId = String(productId || productData?.id || '');
    if (!productId) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    if (productData && Object.keys(productData).length > 0) {
      const viewedDetails = this.getProductRecords().filter((product) => String(product.id) !== productId);
      viewedDetails.unshift({
        id: productId,
        handle: productData.handle || '',
        title: productData.title || '',
        url: productData.url || '',
        image: productData.image || '',
        price: productData.price || '',
      });

      localStorage.setItem(this.#DETAILS_KEY, JSON.stringify(viewedDetails.slice(0, this.#MAX_PRODUCTS)));
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#DETAILS_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const products = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      return Array.isArray(products) ? products.map(String) : [];
    } catch (error) {
      console.warn('Could not read recently viewed products.', error);
      return [];
    }
  }

  /**
   * Retrieves card-ready recently viewed product records.
   * @returns {{ id: string, handle: string, title: string, url: string, image: string, price: string }[]}
   */
  static getProductRecords() {
    try {
      const products = JSON.parse(localStorage.getItem(this.#DETAILS_KEY) || '[]');
      if (!Array.isArray(products)) return [];

      return products
        .filter((product) => product && product.id && product.title && product.url)
        .map((product) => ({
          id: String(product.id),
          handle: product.handle || '',
          title: product.title || '',
          url: product.url || '',
          image: product.image || '',
          price: product.price || '',
        }));
    } catch (error) {
      console.warn('Could not read recently viewed product details.', error);
      return [];
    }
  }
}
